import { createAdminUserApi, updateAdminUserApi } from "@/api/services/admin/user";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { USER_ACTIVE_STATUS } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { adminUserSchema } from "@/features/admin-user/schema/adminUserSchema";
import { TAdminUserForm } from "@/features/admin-user/types/adminUserForm";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const initCreateForm: TAdminUserForm = {
  fullName: "",
  email: "",
  adminRole: "",
  activeStatus: USER_ACTIVE_STATUS.ACTIVE,
};

type TAdminUserFormProps = {
  readonly?: boolean;
  previousData?: Partial<TAdminUserForm>;
};

export interface AdminUserFormRef {
  submitForm: () => void;
}

export const AdminUserForm = forwardRef<AdminUserFormRef, TAdminUserFormProps>(({ readonly, previousData }, ref) => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const methods = useForm<TAdminUserForm>({
    defaultValues: previousData ?? initCreateForm,
    resolver: yupResolver(adminUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  // Expose submit method to parent component
  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      await methods.handleSubmit(onSubmit)();
    },
  }));

  useEffect(() => {
    if (!methods.formState.isDirty) {
      methods.reset(previousData ?? initCreateForm);
    }
  }, [previousData]);

  const onSubmit = async (data: TAdminUserForm) => {
    try {
      if (id) {
        const rs = await updateAdminUserApi(id, data);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL, id] });
        navigate(getDynamicRoute(AppRoutes.adminUserDetail, { id: id }));
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createAdminUserApi(data);
        navigate(AppRoutes.adminUser);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_LIST] });
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={adminUserSchema}>
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid item xs={6}>
          <FormField control={control} name="fullName">
            <Input readonly={readonly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="email">
            <Input readonly={readonly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="adminRole">
            <Select readonly={readonly} enumName="EAdminRole" />
          </FormField>
        </Grid>
      </Grid>
    </Form>
  );
});
